import React, { ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';

import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { reactNodeToString } from '@gumtree/shared/src/util/ga4-bff';

import { toggleFullscreenFilter } from '@gumtree/shared/src/reducers/fullscreen-filter';
import type { ShellUseDispatch } from '@gumtree/shell/src/reducers/common';

import Icon from '../icon/icon';

import { AccordionContainer, AccordionHeader } from './styles';

export default function Accordion({
    title,
    isDefaultOpened = false,
    isFullscreenFilter = false,
    children,
    onOpenChange,
}: AccordionProps) {
    const dispatch = useDispatch() as ShellUseDispatch;
    const [isOpened, setIsOpened] = useState(isDefaultOpened);

    const toggle = () =>
        setIsOpened((curr) => {
            const next = !curr;
            trackGA4Event<GA4.AccordionEvent>({
                event: next ? 'accordion_open' : 'accordion_close',
                accordionTitle: reactNodeToString(title, React.isValidElement),
            });
            onOpenChange?.(next);
            return next;
        });

    const openFullscreenFilter = () => dispatch(toggleFullscreenFilter());

    const handleToggleAccordion = () => {
        if (isFullscreenFilter) {
            openFullscreenFilter();
        }

        toggle();
    };

    return (
        <AccordionContainer>
            <AccordionHeader onClick={handleToggleAccordion}>
                <div>{title}</div>
                {isFullscreenFilter ? (
                    <Icon type="chevron-r" />
                ) : isOpened ? (
                    <Icon type="chevron-u" />
                ) : (
                    <Icon type="chevron-d" />
                )}
            </AccordionHeader>

            {isOpened && !isFullscreenFilter && <div className="filter-content">{children}</div>}
        </AccordionContainer>
    );
}

interface AccordionProps {
    title: ReactNode;
    isDefaultOpened?: boolean;
    isFullscreenFilter?: boolean;
    children?: React.ReactElement | string;
    /** Called when the open state is changed. */
    onOpenChange?: (open: boolean) => void;
}
